import { CompositeLayer } from '@deck.gl/core'
import { PathLayer, ScatterplotLayer, IconLayer } from '@deck.gl/layers'
import { groupData } from '@/js/analyze/GroupData.js'

const defaultProps = {
  ...CompositeLayer.defaultProps,
  groupKey: 'group',
  highlightedUserId: { type: 'string', value: null },
}

/**
 * 軌跡表示用カスタムレイヤー (静的)
 *
 * [ハイライト実装アプローチ]
 * このレイヤーは、パフォーマンスを考慮し、`updateState`内で一度だけデータを
 * 「通常用」と「ハイライト用」に分割して`state`に保持するアプローチを採用しています。
 * `renderLayers`では、ハイライト用レイヤーを通常用レイヤーの後に描画することで、
 * `render`のたびに高コストなソート処理を行うのを避けつつ、
 * ハイライト対象を最前面に表示します。
 */
class LocationTrackCompositeLayer extends CompositeLayer {
  updateState({ props, oldProps, context, changeFlags }) {
    super.updateState({ props, oldProps, context, changeFlags })
    const { propsChanged } = changeFlags

    const shouldUpdate =
      changeFlags.dataChanged ||
      (propsChanged &&
        (props.groupKey !== oldProps.groupKey ||
          props.highlightedUserId !== oldProps.highlightedUserId))

    if (shouldUpdate) {
      this._updateTrackData()
      this.setNeedsRedraw()
    }
  }

  _updateTrackData() {
    const { data, groupKey, highlightedUserId } = this.props

    let trackData = groupData(data, groupKey)

    // highlightedUserId が指定されている場合、該当するデータを配列の最後に移動する
    // sortより効率的なreduceで並び替え。ハイライト対象を後方の配列に分ける
    const [othersTrackData, highlightedTrackData] =
      highlightedUserId !== null
        ? trackData.reduce(
            ([o, h], item) => {
              if (item[groupKey] === highlightedUserId) {
                h.push(item)
              } else {
                o.push(item)
              }
              return [o, h]
            },
            [[], []]
          )
        : [trackData, []]

    // パフォーマンスのため、sortの代わりにfilterでデータを分割する
    const rawData = data || []
    const highlightedPointData =
      highlightedUserId === null
        ? []
        : rawData.filter((d) => d.user_id === highlightedUserId)
    const othersPointData =
      highlightedUserId === null
        ? rawData
        : rawData.filter((d) => d.user_id !== highlightedUserId)

    this.setState({
      // --- 通常表示用データ ---
      othersTrackData,
      othersPointData,
      othersStartData: othersTrackData.map((d) => d.data[0]),
      othersEndData: othersTrackData.map((d) => d.data[d.data.length - 1]),
      // --- ハイライト用データ ---
      highlightedTrackData,
      highlightedPointData,
      highlightedStartData: highlightedTrackData.length
        ? highlightedTrackData.map((d) => d.data[0])
        : othersTrackData.map((d) => d.data[0]),
      highlightedEndData: highlightedTrackData.length
        ? highlightedTrackData.map((d) => d.data[d.data.length - 1])
        : othersTrackData.map((d) => d.data[d.data.length - 1]),
    })
  }

  renderLayers() {
    return [
      // --- 通常表示用レイヤー ---

      // 測位軌跡
      new PathLayer(
        { getPath: (d) => d.data.map((v) => v.coordinates) },
        this.getSubLayerProps({ id: 'line' }),
        {
          id: `${this.props.id}-line-others`,
          data: this.state.othersTrackData,
        }
      ),
      // 測位地点
      new ScatterplotLayer(this.getSubLayerProps({ id: 'point' }), {
        id: `${this.props.id}-point-others`,
        data: this.state.othersPointData,
      }),
      // 測位始点
      new IconLayer(this.getSubLayerProps({ id: 'start' }), {
        id: `${this.props.id}-start-others`,
        data: this.state.othersStartData,
      }),
      // 測位終点
      new IconLayer(this.getSubLayerProps({ id: 'end' }), {
        id: `${this.props.id}-end-others`,
        data: this.state.othersEndData,
      }),

      // --- ハイライト用レイヤー ---
      // 通常レイヤーの後に描画することで、最前面に表示される

      // 測位軌跡
      new PathLayer(
        { getPath: (d) => d.data.map((v) => v.coordinates) },
        this.getSubLayerProps({ id: 'line' }), // 'line' のスタイルを継承
        {
          id: `${this.props.id}-line-highlighted`,
          data: this.state.highlightedTrackData,
        }
      ),
      // 測位地点
      new ScatterplotLayer(
        this.getSubLayerProps({ id: 'point' }), // 'start' のスタイルを継承
        {
          id: `${this.props.id}-point-highlighted`,
          data: this.state.highlightedPointData,
        }
      ),
      // 測位始点
      new IconLayer(
        this.getSubLayerProps({ id: 'start' }), // 'start' のスタイルを継承
        {
          id: `${this.props.id}-start-highlighted`,
          data: this.state.highlightedStartData,
        }
      ),
      // 測位終点
      new IconLayer(
        this.getSubLayerProps({ id: 'end' }), // 'end' のスタイルを継承
        {
          id: `${this.props.id}-end-highlighted`,
          data: this.state.highlightedEndData,
        }
      ),
    ]
  }
}

LocationTrackCompositeLayer.layerName = 'LocationTrackCompositeLayer'
LocationTrackCompositeLayer.defaultProps = defaultProps

export { LocationTrackCompositeLayer }
